import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { myPagePath } from '../utils/myPage';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import BetaForm from '../BetaForm';
import { API } from '../utils/constants';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [showSignUpDialog, setShowSignUpDialog] = useState(false);
  const [showBetaDialog, setShowBetaDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    confirmPassword: "",
    accessCode: ""
  });

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email || !formData.password || !formData.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/register`, {
        email: formData.email,
        first_name: formData.firstName,
        password: formData.password,
        access_code: formData.accessCode
      });
      
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setShowSignUpDialog(false);
        navigate(myPagePath());
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Registration failed";
      toast.error(errorMsg);
    }
  };

  return (
    <div style={{ backgroundColor: '#EEE7D8', minHeight: '100vh' }}>
      {/* Header Navigation */}
      <div className="px-4 md:px-8 py-4 md:py-6 flex justify-between items-center relative z-10">
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_recipehub-15/artifacts/wndzktxu_wellby-logo.svg"
            alt="Wellby"
            style={{ 
              height: '60px',
              width: 'auto'
            }}
          />
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowSignUpDialog(true)} 
            variant="outline"
            className="px-4 py-2 md:px-6 md:py-3 font-medium text-sm md:text-base hover-blush transition-colors focus:outline-none focus:ring-0"
            style={{ 
              borderColor: '#51120A',
              color: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif",
              borderWidth: '1px'
            }}
          >
            Sign Up
          </Button>
          <Button 
            onClick={() => setShowBetaDialog(true)} 
            className="px-4 py-2 md:px-6 md:py-3 text-white font-medium text-sm md:text-base"
            style={{ 
              backgroundColor: '#51120A',
              borderColor: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            Join Beta
          </Button>
        </div>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px' }}>

        {/* Content */}
        <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
          <h1 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '48px', marginBottom: '16px' }}>
            Privacy Policy
          </h1>
          
          <p style={{ fontSize: '16px', marginBottom: '40px', opacity: 0.7 }}>
            Last updated: December 14, 2025
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '32px' }}>
            Welcome to Wellby ("Wellby," "we," "us," or "our"). Your privacy matters to us. This Privacy Policy explains how we collect, use, and protect your information when you use the Wellby website, application, and related services (collectively, the "Service").
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            By using Wellby, you agree to the practices described below.
          </p>

          {/* Information We Collect */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Information We Collect
          </h2>
          
          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '24px' }}>
            We collect only the information necessary to provide and improve the Service.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', marginTop: '32px' }}>
            1. Information You Provide
          </h3>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Account information (such as your email address) when you sign up for Wellby</li>
            <li style={{ marginBottom: '12px' }}>Recipes and content you choose to add to your account, including ingredients, instructions, notes, photos, and categories</li>
            <li style={{ marginBottom: '12px' }}>Beta waitlist information, including any details you submit through forms (this information is sent directly to us via email)</li>
            <li style={{ marginBottom: '12px' }}>Profile information you choose to make public, such as your name, bio, and social links</li>
          </ul>

          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', marginTop: '32px' }}>
            2. Automatically Collected Information
          </h3>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            We may collect limited technical information such as:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Device type and browser</li>
            <li style={{ marginBottom: '12px' }}>Basic usage data (e.g., pages viewed, actions taken)</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            This helps us understand how the Service is used and improve performance.
          </p>

          {/* How We Use Your Information */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            How We Use Your Information
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            We use your information to:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Create and manage your Wellby account</li>
            <li style={{ marginBottom: '12px' }}>Store and display your recipes and content</li>
            <li style={{ marginBottom: '12px' }}>Enable sharing of public recipe pages when you choose to share them</li>
            <li style={{ marginBottom: '12px' }}>Communicate with you about beta access, updates, or support</li>
            <li style={{ marginBottom: '12px' }}>Improve and develop Wellby features and functionality</li>
          </ul>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', fontWeight: '600' }}>
            We do not sell your personal information.
          </p>

          {/* Sharing Your Information */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Sharing Your Information
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
            We only share your information in limited circumstances:
          </p>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}><strong>Public content:</strong> Recipes or profiles you choose to make public can be viewed by others via shared links</li>
            <li style={{ marginBottom: '12px' }}><strong>Service providers:</strong> We may use trusted tools or services to help operate Wellby (such as hosting or email delivery)</li>
            <li style={{ marginBottom: '12px' }}><strong>Legal requirements:</strong> If required by law or to protect the safety and rights of Wellby and its users</li>
          </ul>

          {/* Your Choices & Control */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Your Choices & Control
          </h2>

          <ul style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>You can edit or delete your recipes and profile information at any time</li>
            <li style={{ marginBottom: '12px' }}>You control what content is public versus private</li>
            <li style={{ marginBottom: '12px' }}>You may request deletion of your account by contacting us</li>
          </ul>

          {/* Data Security */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Data Security
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            We take reasonable measures to protect your information. However, no online service can guarantee absolute security, and you use Wellby at your own risk.
          </p>

          {/* Children's Privacy */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Children's Privacy
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            Wellby is not intended for children under 13. We do not knowingly collect personal information from children.
          </p>

          {/* Changes to This Policy */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Changes to This Policy
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '40px' }}>
            We may update this Privacy Policy from time to time. If we make changes, we'll update the "Last updated" date at the top of this page.
          </p>

          {/* Contact Us */}
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '32px', marginBottom: '16px', marginTop: '40px' }}>
            Contact Us
          </h2>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
            If you have questions or concerns about this Privacy Policy or your data, please contact:
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
            <strong>Wellby</strong>
          </p>

          <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '60px' }}>
            Email: <a href="mailto:britt@getwellby.com" style={{ color: '#875C66', textDecoration: 'underline' }}>britt@getwellby.com</a>
          </p>
        </div>
      </div>

      {/* Sign Up Dialog */}
      <Dialog open={showSignUpDialog} onOpenChange={setShowSignUpDialog}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", color: '#52120A' }}>Sign Up</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Create your account to access the recipe manager</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignUp} className="space-y-4">
            <div>
              <Label htmlFor="accessCode" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Invite Code</Label>
              <Input
                id="accessCode"
                type="text"
                value={formData.accessCode || ''}
                onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                required
                placeholder="Enter your invite code"
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="firstName" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="email" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="password" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <Button type="submit" className="w-full" style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Sign Up
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Beta Form Dialog */}
      <BetaForm open={showBetaDialog} onOpenChange={setShowBetaDialog} />
    </div>
  );
};

export default PrivacyPolicy;
